import classnames from 'classnames';
import { useState, useEffect } from 'react';
import Button from '../Button';
import './render.scss';
import { getBannerOperationExtraAttr, isValid } from '../../scripts/utils';
import MainTitle from '../Common/main-title';
import IconAiArrow from '../../components/IconWrapper/arrow-ai';
import VideoIcon from '../Video/video-icon';
import { createVideoModal } from '../Video/video';
import FileDown from '../IconWrapper/file-down';
import ForwardPlay from '../IconWrapper/forward-play';
import { oss, isVideoFile } from '../../scripts/utils';
import NoteTips from './NoteTips';
import { isEmpty } from 'lodash';

// 自定义hook：监听窗口宽度变化，超过1440时按2:1宽高比计算高度，同时返回当前宽度
const useResponsiveHeight = (defaultHeight) => {
  const [height, setHeight] = useState(defaultHeight);
  const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 0);

  useEffect(() => {
    const calculateHeight = () => {
      const currentWidth = window.innerWidth;
      setWidth(currentWidth);
      if (currentWidth > 1440) {
        // 宽高比2:1，高度 = 宽度 / 2
        setHeight(currentWidth / 2);
      } else {
        setHeight(defaultHeight);
      }
    };

    // 初始计算
    calculateHeight();

    // 监听窗口大小变化
    window.addEventListener('resize', calculateHeight);

    return () => {
      window.removeEventListener('resize', calculateHeight);
    };
  }, [defaultHeight]);

  return { height, width };
};

export const renderDefault = (props) => {
  const { styles, primaryColor, media, position, theme = 'dark', title, preTitle, subheading, legend, operation, note, noteTips, isPc, forceShowNote } = props;
  const isVideo = isVideoFile(media);
  // 获取默认高度（从styles中提取数字）
  const defaultHeight = typeof styles?.height === 'number'
    ? styles.height
    : parseInt(styles?.height) || 720;
  // 使用响应式高度和宽度
  const { height: responsiveHeight, width } = useResponsiveHeight(defaultHeight);
  // 宽度超过1440时启用居中布局
  const isCentered = width > 1440;
  const hasNote = (isVideo || forceShowNote) && note;
  const hasNoteTips = hasNote && !isEmpty(noteTips);

  // 抽取公共内容
  const children = (
    <>
      <div
        className={classnames('comp-content', 'banner-content', 'default-banner-content', {
          [position]: position,
          [theme]: theme
        })}
      >
        {preTitle && <div className="banner-pre-title" >{preTitle}</div>}
        <div className="banner-title-wrapper">
          <div className="banner-title">{title}</div>
          {isValid(subheading) ? <div className="banner-sub-title">{subheading}</div> : null}
        </div>
        <div className="banner-legend" dangerouslySetInnerHTML={{ __html: legend }} />
        {operation instanceof Array ? (
          <div className="pc-banner-operation-wrapper">
            <div className="banner-operation">
              {operation.map((opt, index) => {
                if (opt.text) {
                  return (
                    <Button key={`banner-operation-${index}`} {...opt.extraAttr} fill={opt.theme} type='black'>
                      {opt.text}
                      <IconAiArrow />
                    </Button>
                  );
                }
                return null;
              })}
            </div>
          </div>
        ) : null}
      </div>
      {hasNote && (() => {
        const noteEl = (
          <div className="banner-note">
            <p className="banner-note-text">{note}</p>
          </div>
        );
        return (
          <div className={classnames('banner-note-tips-row')}>
            {isPc && noteEl}
            {hasNoteTips && isPc && <div className="banner-note-tips-divider" />}
            {hasNoteTips && <NoteTips list={noteTips} isPc={isPc} />}
            {!isPc && noteEl}
          </div>
        );
      })()}
    </>
  );

  return (
    <>
      <div
        className={classnames('img-background', {
          'video-background': isVideo || (forceShowNote && note),
        })}
        style={{
          ...styles,
          height: responsiveHeight,
          backgroundColor: primaryColor,
          ...(!isVideo && { backgroundImage: `url("${oss(media, undefined, 866)}")` })
        }}
      >
        {isVideo && (
          <video
            className="banner-video"
            src={media}
            autoPlay
            loop
            muted
            playsInline
          />
        )}
        {isCentered ? (
          <div className="banner-centered-wrapper">{children}</div>
        ) : (
          children
        )}
      </div>
    </>
  );
};

export const RenderProduction = (props) => {
  const { styles, media, theme, title, legend, position } = props;
  return (
    <div
      className={classnames('comp-root', 'product-banner-wrapper')}
      style={{
        ...styles,
        display: 'none',
        backgroundColor: '#fff'
      }}
    >
      <div
        className={classnames('comp-content banner-custom-content', {
          [theme]: theme
        })}
      >
        <MainTitle theme={theme} main={title} sub={legend} position={position}/>
        <img className="production-image" src={media} alt="production" />
      </div>
    </div>
  );
};

export const RenderDetailHeader = (props) => {
  const { styles, theme, title, legend, src, position } = props;
  return (
    <div
      className={classnames('comp-root', 'detail-header-wrapper', position)}
      style={{
        ...styles,
        backgroundColor: '#fff',
        backgroundPosition: '50% 50%',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundImage: `url("${oss(src, undefined,  866)}")`
      }}
    >
      <div
        className={classnames('title-wrapper', position, {
          [theme]: theme,
        })}
      >
        <MainTitle theme={theme} className="title-content" size="xl" main={title} sub={legend} position={position}/>
      </div>
    </div>
  );
};

export const renderCaseDetail = (props) => {
  const { styles, media, theme, title, video, i18n } = props;
  return (
    <div
      className={classnames('comp-root', 'banner-case-detail-wrapper')}
      style={{
        ...styles,
        backgroundImage: `url("${oss(media, undefined,  866)}")`
      }}
    >
      <div
        className={classnames('title-wrapper', {
          [theme]: theme
        })}
      >
        <div className="banner-case-detail-title">{title}</div>
        {video ? (
          <>
            <div className="flex-center">
              <VideoIcon className="case-video-icon" onClick={() => createVideoModal(video)} />
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export const renderPlateDetail = (props) => {
  const { styles, media, theme, title, legend, operation, position } = props;
  const { jumpBO, demonstration } = operation ?? {};
  return (
    <div
      className={classnames('comp-root', 'plate-detail-wrapper', position)}
      style={{
        ...styles,
        backgroundColor: '#fff',
        backgroundPosition: '50% 50%',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundImage: `url("${oss(media, undefined,  866)}")`
      }}
    >
      <div
        className={classnames('title-wrapper', position, {
          [theme]: theme
        })}
      >
        <MainTitle theme={theme} className="title-content" size="xl" main={title} sub={legend} position={position} />
      </div>
      <div className={classnames('plate-footer', position)}>
        {jumpBO ? (
          <Button
            target="_blank"
            {...getBannerOperationExtraAttr({ jumpBO })}
            className="footer-button"
            prefix={<FileDown size={16} fill={'#fff'} />}
            suffix={false}
          >
            {jumpBO?.buttonTitle}
          </Button>
        ) : null}
        {demonstration ? (
          <Button
            type="white"
            {...getBannerOperationExtraAttr({ jumpBO: demonstration })}
            className="footer-button"
            prefix={<ForwardPlay size={16} fill={'#9196AA'} />}
            suffix={false}
          >
            {demonstration.buttonTitle}
          </Button>
        ) : null}
      </div>
    </div>
  );
};

export const RenderSolutionDetail = (props) => {
  const { styles, media, theme, title, legend, operation } = props;
  const { jumpBO, industry } = operation;
  return (
    <div
      className={classnames('comp-root', 'solution-detail-wrapper')}
      style={{
        ...styles,
        backgroundColor: '#fff',
        backgroundPosition: '50% 50%',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundImage: `url("${oss(media, undefined,  866)}")`
      }}
    >
      <div
        className={classnames('title-wrapper', {
          [theme]: theme
        })}
      >
        <MainTitle theme={theme} className="title-content" size="xl" main={title} sub={legend} />
      </div>
      <div className="solution-footer">
        <Button
          {...getBannerOperationExtraAttr({ jumpBO })}
          className={` ${getBannerOperationExtraAttr({ jumpBO })?.className || ''} ${getBannerOperationExtraAttr({ jumpBO })?.type === 'video' ? 'btn-video' : 'btn-main'} footer-button`
          }
        >{jumpBO?.buttonTitle}</Button>
        {industry ? (
          <Button
            type="default"
            className="form footer-button"
            prefix={<ForwardPlay size={14} fill={'#fff5'} />}
            suffix={false}
            fill="#fff"
            style={{ borderColor: '#fff5' }}
            {...getBannerOperationExtraAttr({ jumpBO: industry })}
          >
            {industry.buttonTitle}
          </Button>
        ) : null}
      </div>
    </div>
  );
};
